import React, { useEffect, useState } from 'react';
import { dashboardService, PastilhaDeForma, RecebimentosDoMes as Leitura } from '../../services/api';
import { A_PROPRIA_LISTA_DIZ_O_VAZIO, EstadoDaLista, useEstadoDaCarga, textos } from '../../design';
import { fmtDate } from '../../utils/formatters';

/**
 * RECEBIMENTOS DO MÊS no Dashboard (decisão R1 do Heitor, 23/10/2026), lido de GET /dashboard/recebimentos (backend #525).
 *
 * A unidade é o PAGAMENTO, não a fatura. O card antigo lia o resumo de recebíveis, que soma o valor pago das FATURAS de
 * competência do mês: em outubro isso inflava R$ 4.420,00 nas três faturas de NF parcial, pagas aos pedaços, e as
 * pastilhas contavam faturas abertas. Agora o total é a soma dos pagamentos do mês, e `faturas` é a contagem distinta.
 *
 * A forma: o pagamento sem forma anotada herda a da fatura (o backend marca `herdadas`/`formaHerdada`). A tela diz isso
 * em vez de esconder: nota sob as pastilhas, e o title de cada pastilha e de cada linha.
 */
const ROTULO_DA_FORMA: Record<string, string> = {
  boleto: 'Boleto', ted: 'TED', pix: 'PIX', transferencia: 'Transferência', outros: 'Outros',
};
const SEM_FORMA = 'Sem forma';

/** O rótulo da forma. Em produção há 'ted' e 'TED' (mesma forma): normaliza para minúscula antes de mapear. */
export function rotuloDaForma(forma: string | null | undefined): string {
  const k = String(forma ?? '').trim().toLowerCase();
  if (!k || k === 'undefined' || k === 'null') return SEM_FORMA;
  return ROTULO_DA_FORMA[k] || String(forma).trim();
}

export interface PastilhaNaTela {
  rotulo: string;
  valor: number;
  pagamentos: number;
  herdadas: number;
}

/** As pastilhas do mês: a mesma forma somada (o backend agrupa pela grafia crua), só com valor, da maior para a menor. */
export function pastilhasDoMes(porForma: PastilhaDeForma[] | null | undefined): PastilhaNaTela[] {
  const somadas: Record<string, PastilhaNaTela> = {};
  for (const p of porForma || []) {
    const rotulo = rotuloDaForma(p?.forma);
    const s = (somadas[rotulo] = somadas[rotulo] || { rotulo, valor: 0, pagamentos: 0, herdadas: 0 });
    s.valor = Math.round((s.valor + (Number(p?.valor) || 0)) * 100) / 100;
    s.pagamentos += Number(p?.pagamentos) || 0;
    s.herdadas += Number(p?.herdadas) || 0;
  }
  return Object.values(somadas).filter((p) => p.valor > 0).sort((a, b) => b.valor - a.valor);
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const nomeDoMes = (mes: string) => { const [a, m] = mes.split('-'); return `${MESES[Number(m) - 1] || m} de ${a}`; };

/** A resposta tem a forma combinada? Fora dela (rota velha, proxy, HTML de erro), é falha de leitura, não tela quebrada. */
export function eLeituraValida(r: any): r is Leitura {
  return !!r && typeof r === 'object' && !Array.isArray(r) && typeof r.mes === 'string' && /^\d{4}-\d{2}$/.test(r.mes)
    && Number.isFinite(Number(r.total)) && Number.isFinite(Number(r.pagamentos)) && Number.isFinite(Number(r.faturas))
    && Array.isArray(r.porForma) && Array.isArray(r.ultimos);
}

export default function RecebimentosDoMes() {
  const { rodar, ...carga } = useEstadoDaCarga();
  const [lido, setLido] = useState<Leitura | null>(null);

  const ler = () => rodar(async () => {
    const r = await dashboardService.recebimentos();
    if (!eLeituraValida(r)) throw new Error('a resposta não veio no formato esperado');
    setLido(r);
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ler(); }, []);

  const pastilhas = pastilhasDoMes(lido?.porForma);
  const herdadas = pastilhas.reduce((s, p) => s + p.herdadas, 0);
  const ultimos = (lido?.ultimos || []).slice(0, 5);

  return (
    <div className='bg-central-cartao rounded-central border border-central-borda overflow-hidden' data-cartao='recebimentos'>
      <div className='p-4 border-b border-central-borda flex items-center justify-between gap-3'>
        <div>
          <h3 className='text-central-texto font-semibold'>Recebimentos do Mês</h3>
          {lido && (
            <p className='text-central-texto-fraco text-sm' data-recebimentos='conta'>
              {nomeDoMes(lido.mes)} · {plural(lido.pagamentos, 'pagamento', 'pagamentos')} em {plural(lido.faturas, 'fatura', 'faturas')}
            </p>
          )}
        </div>
        <p className='text-2xl font-bold text-central-texto tabular-nums' data-recebimentos='total'>{lido ? `R$ ${fmt(lido.total)}` : textos.numeroSemFonte}</p>
      </div>
      <div className='p-4'>
        <EstadoDaLista carga={carga} temDados={!!lido} oQue='os recebimentos do mês' onTentarDeNovo={ler} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
          {lido && lido.pagamentos === 0 ? (
            <p className='text-central-texto-fraco text-sm' data-recebimentos='vazio'>Nenhum pagamento recebido neste mês até agora.</p>
          ) : (
            <>
              <div className='flex flex-wrap gap-2'>
                {pastilhas.map((p) => (
                  <span key={p.rotulo} data-pastilha
                    title={p.herdadas > 0 ? `${plural(p.herdadas, 'pagamento', 'pagamentos')} sem forma anotada: a forma é a da fatura` : undefined}
                    className='inline-flex items-baseline gap-1.5 px-3 py-1 rounded-full bg-central-elevado border border-central-borda text-sm'>
                    <span className='text-central-texto-fraco'>{p.rotulo}</span>
                    <span className='text-central-texto font-semibold tabular-nums'>R$ {fmt(p.valor)} · {p.pagamentos} pag.</span>
                  </span>
                ))}
              </div>
              {herdadas > 0 && (
                <p className='text-central-texto-fraco text-xs mt-2' data-recebimentos='herdadas'>
                  {herdadas === 1 ? '1 pagamento não tem a forma anotada; a forma mostrada é a da fatura.' : `${herdadas} pagamentos não têm a forma anotada; a forma mostrada é a da fatura.`}
                </p>
              )}
              {ultimos.length > 0 && (
                <div className='mt-4'>
                  <p className='text-central-texto-fraco text-xs uppercase font-medium mb-2'>Últimos recebimentos</p>
                  <div className='space-y-2'>
                    {ultimos.map((u, i) => (
                      <div key={`${u.invoiceId}-${u.data}-${i}`} className='flex items-center justify-between gap-3 py-1.5 border-b border-central-borda last:border-0' data-ultimo>
                        <div className='flex items-center gap-3 min-w-0'>
                          <span className='text-central-texto-fraco text-xs font-mono'>{u.code || textos.numeroSemFonte}</span>
                          <span className='text-central-texto text-sm truncate'>{u.cliente || textos.numeroSemFonte}</span>
                        </div>
                        <div className='flex items-center gap-3 flex-shrink-0'>
                          <span className='text-central-texto-fraco text-xs'>{fmtDate(u.data)}</span>
                          <span className='text-central-texto-fraco text-xs' title={u.formaHerdada ? 'Sem forma anotada no pagamento: é a forma da fatura' : undefined}>
                            {rotuloDaForma(u.forma)}{u.formaHerdada ? '*' : ''}
                          </span>
                          <span className='text-central-texto font-semibold text-sm tabular-nums'>R$ {fmt(u.valor)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {ultimos.some((u) => u.formaHerdada) && <p className='text-central-texto-fraco text-xs mt-2'>* forma da fatura</p>}
                </div>
              )}
            </>
          )}
        </EstadoDaLista>
      </div>
    </div>
  );
}
